import { countryCodes } from "~~/i18n/enums/countryCodes";
import type { VatFormatConfig } from "~~/types/config/VatFormatConfig";
import { DIGITS_AND_LETTERS, DIGITS_LETTERS_AND_SPECIAL_CHARS, DIGITS_ONLY } from "~~/constants/regex";
import type { CountryCodeValue } from "~~/types/localization/localizationHelperTypes";

export const SLUG_COD_FEE: string = 'cod-fee';
export const COD_FEE_INFO_AMOUNT_NAME: string = 'cod_fee_amount';
export const COD_FEE_INFO_ENABLED: string = 'cod_fee_enabled';
export const COD_FEE_INFO_TITLE: string = 'cod_fee_title';
export const BANK_PAYMENT_BIC: string = 'bank_transfer_payment_data_bic_or_swift';
export const BANK_PAYMENT_ACCOUNT_OWNER = 'bank_transfer_payment_data_account_owner';
export const BANK_PAYMENT_INSTITUTE = 'bank_transfer_payment_data_bank_institute';
export const BANK_PAYMENT_IBAN = 'bank_transfer_payment_data_iban';
export const REGISTERED_CUSTOMER_TAB = 'returningUserTab';

export const ADYEN_PAYMENT_METHOD_MULTIBANCO = 'multibanco';

export const ONLINEBANKING_PL_NAME = 'Przelewy online / BLIK';

export const CART_PROMISE_QUEUE_KEY = 'cartPromiseQueue';

export const DAYS_UNTIL_ORDER_CANCELLATION = '6';

export const RETURN_PORTAL_URL = 'https://shop.expondo.returns.cloud/#/';
export const QUICK_ADD_TO_CART_RESTRICTED_COUNTRIES = [countryCodes.AT, countryCodes.DE];
export const CHECKOUT_DISABLED = [
  countryCodes.IE,
  countryCodes.GR,
];
export const MAX_BANK_AND_CASH_PAYMENT_ITEMS = 20;

export const GOOGLE_AUTOCOMPLETE_CITY_FIELDS_TO_USE: Record<string, string[]> & { defaultFields: string[] } = {
  [countryCodes.SK]: ['postal_town', 'sublocality_level_1', 'administrative_area_level_2'],
  [countryCodes.CZ]: ['postal_town', 'sublocality_level_1', 'administrative_area_level_2'],
  defaultFields: ['postal_town', 'locality', 'administrative_area_level_1'],
};

export const VAT_FORMAT: VatFormatConfig = {
  [countryCodes.PL]: {
    rule: DIGITS_ONLY,
    placeholder: '1234567890',
    prefix: 'PL',
  },
  [countryCodes.AT]: {
    rule: DIGITS_AND_LETTERS,
    placeholder: 'U12345678',
    prefix: 'AT',
  },
  [countryCodes.BE]: {
    rule: DIGITS_ONLY,
    placeholder: '1234567890',
    prefix: 'BE',
  },
  [countryCodes.SK]: {
    rule: DIGITS_ONLY,
    placeholder: '1234567890',
    prefix: 'SK',
  },
  [countryCodes.IE]: {
    rule: DIGITS_AND_LETTERS,
    placeholder: '1234567XA',
    prefix: 'IE',
  },
  [countryCodes.DE]: {
    rule: DIGITS_ONLY,
    placeholder: '123456789',
    prefix: 'DE',
  },
  [countryCodes.PT]: {
    rule: DIGITS_ONLY,
    placeholder: '123456789',
    prefix: 'PT',
  },
  [countryCodes.IT]: {
    rule: DIGITS_ONLY,
    placeholder: '12345678901',
    prefix: 'IT',
  },
  [countryCodes.FR]: {
    rule: DIGITS_AND_LETTERS,
    placeholder: 'XX123456789',
    prefix: 'FR',
  },
  [countryCodes.HU]: {
    rule: DIGITS_ONLY,
    placeholder: '12345678',
    prefix: 'HU',
    additionalVatNumberProcessing: (vatNumber) => {

      return vatNumber.substring(0, 8);

    },
  },
  [countryCodes.CZ]: {
    rule: DIGITS_ONLY,
    placeholder: '1234567890',
    prefix: 'CZ',
  },
  [countryCodes.NO]: {
    rule: DIGITS_AND_LETTERS,
    placeholder: '123456789MVA',
    prefix: 'NO',
  },
  [countryCodes.NL]: {
    rule: DIGITS_AND_LETTERS,
    placeholder: '123456789BXX',
    prefix: 'NL',
  },
  [countryCodes.CH]: {
    rule: DIGITS_LETTERS_AND_SPECIAL_CHARS,
    placeholder: '123.456.789XXXX',
    prefix: 'CHE',
  },
  [countryCodes.DK]: {
    rule: DIGITS_ONLY,
    placeholder: '12345678',
    prefix: 'DK',
  },
  [countryCodes.SE]: {
    rule: DIGITS_ONLY,
    placeholder: '123456789012',
    prefix: 'SE',
  },
  [countryCodes.ES]: {
    rule: DIGITS_AND_LETTERS,
    placeholder: 'X1234567X',
    prefix: 'ES',
  },
  [countryCodes.GB]: {
    rule: DIGITS_LETTERS_AND_SPECIAL_CHARS,
    placeholder: '123456789,XX123',
    prefix: 'GB',
  },
  [countryCodes.FI]: {
    rule: DIGITS_ONLY,
    placeholder: '12345678',
    prefix: 'FI',
  },
  [countryCodes.RO]: {
    rule: DIGITS_ONLY,
    placeholder: '1234567890',
    prefix: 'RO',
  },
  [countryCodes.LT]: {
    rule: DIGITS_ONLY,
    placeholder: '123456789012',
    prefix: 'LT',
  },
  [countryCodes.SI]: {
    rule: DIGITS_ONLY,
    placeholder: '12345678',
    prefix: 'SI',
  },
  [countryCodes.BG]: {
    rule: DIGITS_ONLY,
    placeholder: '1234567890',
    prefix: 'BG',
  },
  [countryCodes.GR]: {
    rule: DIGITS_ONLY,
    placeholder: '123456789',
    prefix: 'EL',
  },
  [countryCodes.HR]: {
    rule: DIGITS_ONLY,
    placeholder: '12345678901',
    prefix: 'HR',
  },
};
export const POSTAL_CODE_FORMAT: { [key in countryCodes]: RegExp } = {
  [countryCodes.AT]: /^[1-9]{1}\d{3}$/,
  [countryCodes.BE]: /^[1-9]{1}\d{3}$/,
  [countryCodes.BG]: /^\d{4}$/,
  [countryCodes.CH]: /^\d{4}$/,
  [countryCodes.CZ]: /^\d{3}\s?\d{2}$/,
  [countryCodes.DE]: /^\d{5}$/,
  [countryCodes.DK]: /^\d{4}$/,
  [countryCodes.ES]: /^\d{5}$/,
  [countryCodes.FI]: /^\d{5}$/,
  [countryCodes.FR]: /^\d{5}$/,
  [countryCodes.GB]: /^(GIR ?0AA|[a-zA-Z]{1,2}[0-9][a-zA-Z0-9]? ?[0-9][a-zA-Z]{2})$/,
  [countryCodes.GR]: /^\d{3}\s?\d{2}$/,
  [countryCodes.HR]: /^\d{5}$/,
  [countryCodes.HU]: /^[1-9]{1}\d{3}$/,
  [countryCodes.IE]: /^(([ac-fhknprtv-yAC-FHKNPRTV-Y]\d{2}|D6W|d6w|D6w|d6W)\s?([0-9ac-fhknprtv-yAC-FHKNPRTV-Y]{4})?)$/,
  [countryCodes.IT]: /^\d{5}$/,
  [countryCodes.LT]: /^\d{5}$/,
  [countryCodes.NL]: /^[1-9][0-9]{3}\s?[a-zA-Z]{2}$/,
  [countryCodes.NO]: /^\d{4}$/,
  [countryCodes.PL]: /^\d{2}-\d{3}$/,
  [countryCodes.PT]: /^\d{4}-\d{3}$/,
  [countryCodes.RO]: /^\d{6}$/,
  [countryCodes.SE]: /^\d{3}\s?\d{2}$/,
  [countryCodes.SI]: /^\d{4}$/,
  [countryCodes.SK]: /^\d{3}\s?\d{2}$/,
};
export const STREET_NUMBER_VALIDATION_DISABLED = {
  [countryCodes.AT]: true,
  [countryCodes.BE]: true,
  [countryCodes.BG]: true,
  [countryCodes.CH]: true,
  [countryCodes.CZ]: false,
  [countryCodes.DE]: true,
  [countryCodes.DK]: true,
  [countryCodes.ES]: true,
  [countryCodes.FI]: true,
  [countryCodes.FR]: true,
  [countryCodes.GB]: true,
  [countryCodes.GR]: true,
  [countryCodes.HR]: true,
  [countryCodes.HU]: true,
  [countryCodes.IE]: true,
  [countryCodes.IT]: true,
  [countryCodes.LT]: true,
  [countryCodes.NL]: true,
  [countryCodes.NO]: true,
  [countryCodes.PL]: false,
  [countryCodes.PT]: true,
  [countryCodes.RO]: false,
  [countryCodes.SE]: true,
  [countryCodes.SI]: true,
  [countryCodes.SK]: true,
};
export const MERCHANT_ID = {
  [countryCodes.FR]: 131727769,
  [countryCodes.AT]: 133098273,
  [countryCodes.HU]: 132913488,
  [countryCodes.GB]: 133098834,
  [countryCodes.BE]: 133099047,
  [countryCodes.CH]: 133099011,
  [countryCodes.DK]: 133099140,
  [countryCodes.NO]: 133099242,
  [countryCodes.ES]: 133099185,
  [countryCodes.DE]: 133109434,
  [countryCodes.PT]: 133099281,
  [countryCodes.SE]: 133109491,
  [countryCodes.CZ]: 133109779,
  [countryCodes.NL]: 133111811,
  [countryCodes.IE]: 133112174,
  [countryCodes.PL]: 138563743,
  [countryCodes.FI]: 140415001,
  [countryCodes.SK]: 631950721,
  [countryCodes.GR]: 748405187,
  [countryCodes.SI]: 748412289,
  [countryCodes.LT]: 748423270,
  [countryCodes.BG]: 748436977,
  [countryCodes.IT]: 133098738,
  [countryCodes.RO]: 748202494,
};
export const IS_COMPANY_DEFAULT_CLIENT_TYPE = {
  [countryCodes.AT]: false,
  [countryCodes.BE]: false,
  [countryCodes.BG]: false,
  [countryCodes.CH]: false,
  [countryCodes.CZ]: false,
  [countryCodes.DE]: false,
  [countryCodes.DK]: false,
  [countryCodes.ES]: false,
  [countryCodes.FI]: false,
  [countryCodes.FR]: false,
  [countryCodes.GB]: false,
  [countryCodes.GR]: false,
  [countryCodes.HR]: false,
  [countryCodes.HU]: false,
  [countryCodes.IE]: false,
  [countryCodes.IT]: false,
  [countryCodes.LT]: false,
  [countryCodes.NL]: false,
  [countryCodes.NO]: false,
  [countryCodes.PL]: false,
  [countryCodes.PT]: false,
  [countryCodes.RO]: false,
  [countryCodes.SE]: false,
  [countryCodes.SI]: false,
  [countryCodes.SK]: false,
};

// the price is regarding the local currency of the country e.g EUR, GBP, CHF etc
const cashOnDeliveryPriceLimit = 1500;
const cashOnDeliveryItemLimit = 1;
const cashOnDeliveryItemHidden = 0;
const NOT_SUPPORTED = false;

export const CASH_ON_DELIVERY_PRICE_LIMIT: Record<CountryCodeValue, number> = {
  [countryCodes.SK]: 2500,
  [countryCodes.IE]: cashOnDeliveryPriceLimit,
  [countryCodes.DE]: 1500,
  [countryCodes.PT]: 1000,
  [countryCodes.IT]: 999.99,
  [countryCodes.FR]: 1000,
  [countryCodes.HU]: 499999,
  [countryCodes.CZ]: 60000,
  [countryCodes.AT]: 1500,
  [countryCodes.NO]: cashOnDeliveryPriceLimit,
  [countryCodes.NL]: cashOnDeliveryPriceLimit,
  [countryCodes.CH]: 3500,
  [countryCodes.BE]: 3000,
  [countryCodes.DK]: 20000,
  [countryCodes.SE]: 3000,
  [countryCodes.ES]: 1000,
  [countryCodes.GB]: 2500,
  [countryCodes.FI]: cashOnDeliveryPriceLimit,
  [countryCodes.PL]: 6000,
  [countryCodes.RO]: 7000,
  [countryCodes.LT]: 4500,
  [countryCodes.SI]: 4500,
  [countryCodes.BG]: 8900,
  [countryCodes.GR]: 499,
  [countryCodes.HR]: 4500,
};
export const CASH_ON_DELIVERY_ITEM_LIMIT: Record<CountryCodeValue, number> = {
  [countryCodes.SK]: cashOnDeliveryItemLimit,
  [countryCodes.IE]: cashOnDeliveryItemHidden,
  [countryCodes.DE]: cashOnDeliveryItemHidden,
  [countryCodes.PT]: cashOnDeliveryItemHidden,
  [countryCodes.IT]: cashOnDeliveryItemLimit,
  [countryCodes.FR]: cashOnDeliveryItemHidden,
  [countryCodes.HU]: cashOnDeliveryItemLimit,
  [countryCodes.CZ]: cashOnDeliveryItemLimit,
  [countryCodes.AT]: cashOnDeliveryItemHidden,
  [countryCodes.NO]: cashOnDeliveryItemHidden,
  [countryCodes.NL]: cashOnDeliveryItemHidden,
  [countryCodes.CH]: cashOnDeliveryItemHidden,
  [countryCodes.BE]: cashOnDeliveryItemHidden,
  [countryCodes.DK]: cashOnDeliveryItemHidden,
  [countryCodes.SE]: cashOnDeliveryItemHidden,
  [countryCodes.ES]: cashOnDeliveryItemHidden,
  [countryCodes.GB]: cashOnDeliveryItemHidden,
  [countryCodes.FI]: cashOnDeliveryItemHidden,
  [countryCodes.PL]: cashOnDeliveryItemLimit,
  [countryCodes.RO]: cashOnDeliveryItemLimit,
  [countryCodes.LT]: cashOnDeliveryItemHidden,
  [countryCodes.SI]: cashOnDeliveryItemLimit,
  [countryCodes.BG]: cashOnDeliveryItemHidden,
  [countryCodes.GR]: cashOnDeliveryItemHidden,
  [countryCodes.HR]: cashOnDeliveryItemLimit,
};
export const BANK_PAYMENT_ENABLED: Record<CountryCodeValue, boolean> = {
  [countryCodes.SK]: true,
  [countryCodes.IE]: false,
  [countryCodes.DE]: true,
  [countryCodes.PT]: true,
  [countryCodes.IT]: true,
  [countryCodes.FR]: true,
  [countryCodes.HU]: true,
  [countryCodes.CZ]: true,
  [countryCodes.AT]: true,
  [countryCodes.NO]: true,
  [countryCodes.NL]: false,
  [countryCodes.CH]: true,
  [countryCodes.BE]: true,
  [countryCodes.DK]: true,
  [countryCodes.SE]: false,
  [countryCodes.ES]: true,
  [countryCodes.GB]: false,
  [countryCodes.FI]: true,
  [countryCodes.PL]: true,
  [countryCodes.RO]: true,
  [countryCodes.LT]: true,
  [countryCodes.SI]: true,
  [countryCodes.BG]: true,
  [countryCodes.GR]: false,
  [countryCodes.HR]: false,
};
export const CASH_ON_DELIVERY_FOR_FREIGHT: Record<CountryCodeValue, boolean> = {
  [countryCodes.SK]: true,
  [countryCodes.IE]: false,
  [countryCodes.DE]: false,
  [countryCodes.PT]: false,
  [countryCodes.IT]: false,
  [countryCodes.FR]: false,
  [countryCodes.HU]: true,
  [countryCodes.CZ]: true,
  [countryCodes.AT]: false,
  [countryCodes.NO]: false,
  [countryCodes.NL]: false,
  [countryCodes.CH]: false,
  [countryCodes.BE]: false,
  [countryCodes.DK]: false,
  [countryCodes.SE]: false,
  [countryCodes.ES]: false,
  [countryCodes.GB]: false,
  [countryCodes.FI]: false,
  [countryCodes.PL]: true,
  [countryCodes.RO]: false,
  [countryCodes.LT]: false,
  [countryCodes.SI]: false,
  [countryCodes.BG]: false,
  [countryCodes.GR]: false,
  [countryCodes.HR]: false,
};
export const GOOGLE_ADDRESS_VALIDATION: Record<CountryCodeValue, boolean> = {
  [countryCodes.SK]: false,
  [countryCodes.IE]: false,
  [countryCodes.DE]: false,
  [countryCodes.PT]: NOT_SUPPORTED,
  [countryCodes.IT]: false,
  [countryCodes.FR]: false,
  [countryCodes.HU]: false,
  [countryCodes.CZ]: false,
  [countryCodes.AT]: false,
  [countryCodes.NO]: false,
  [countryCodes.NL]: false,
  [countryCodes.CH]: false,
  [countryCodes.BE]: false,
  [countryCodes.DK]: false,
  [countryCodes.SE]: false,
  [countryCodes.ES]: false,
  [countryCodes.GB]: false,
  [countryCodes.FI]: false,
  [countryCodes.PL]: false,
  [countryCodes.RO]: NOT_SUPPORTED,
  [countryCodes.LT]: false,
  [countryCodes.SI]: NOT_SUPPORTED,
  [countryCodes.BG]: NOT_SUPPORTED,
  [countryCodes.GR]: NOT_SUPPORTED,
  [countryCodes.HR]: NOT_SUPPORTED,
};
export const MAX_SINGLE_PRODUCT_CART_QUANTITY = 1000;

export const ADDRESS_PERSISTENCE_DEBOUNCE_MS = 500;
